import Phaser from 'phaser';
import RandomValuePool from '../common/RandomValuePool';
import ABCyaPhaser from '@abcya/abcya_phaser';
import UIAssetKeys from '../UIAssetKeys';
import Pumpkin from '../game/Pumpkin';
import Leaves from '../game/Leaves';
import CloudMaker from '../game/CloudMaker';
import Bats from '../game/Bats';
import SoundAssetKeys from '../SoundAssetKeys';
import CarveHUD from '../ui/CarveHUD';
import House from '../game/House';
import LocalizationSoundAssetKeys from '../Localization-SoundAssetKeys';

export default class Game extends Phaser.Scene {
  static get KEY() { return 'Game'; }

  init() {
    this.hud = null;

    this.isNighttime = false;

    this.nightSounds = null;

    this.scareWaiter = 8.5;

    this.input.on(Phaser.Input.Events.GAME_OUT, (time, event) => {
      if (event instanceof MouseEvent) {
        this.input.manager.onMouseUp(event);
      }
    });
  }

  create() {
    this.whichPumpkin = new RandomValuePool([1,2,3]);

    this.bg = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._daytime_bg, this).setOrigin(0);
    this.bgNight = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._nighttime_bg, this).setOrigin(0)
      .setVisible(false);
    this.moonGlow = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._MoonGlow, this).setOrigin(0)
      .setVisible(false);

    this.sun = ABCyaPhaser.Images.addImage(524, 0,UIAssetKeys._Sun, this).setOrigin(0, 2);
    this.sun.y = this.sun.height * 2 - 60;

    this.moon = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._Moon, this).setOrigin(0)
      .setInteractive({ pixelPerfect: true })
      .setVisible(false)
      .on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.clickMoon, this);

    this.clouds = new CloudMaker(this);
    this.add.existing(this.clouds);

    this.hills = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._Hills, this).setOrigin(0);
    this.hills.y = this.scale.height - this.hills.height;

    this.hillsNight = ABCyaPhaser.Images.addImage(0,this.hills.y,UIAssetKeys._Hills_Night, this).setOrigin(0)
      .setVisible(false)
      .setInteractive({ pixelPerfect: true }); // blocks clicking on the moon that is behind the hills

    this.fence = ABCyaPhaser.Images.addImage(0,433,UIAssetKeys._Fence, this).setOrigin(0);

    this.grass = ABCyaPhaser.Images.addImage(0,433,UIAssetKeys._Grass, this).setOrigin(0);
    this.grass.y = this.scale.height - this.grass.height;

    this.house = new House(this);
    this.house.x = 18;
    this.house.y = 12;
    this.add.existing(this.house);

    this.tree = ABCyaPhaser.Images.addImage(736,42,UIAssetKeys._Tree, this).setOrigin(0)
      .setInteractive({ pixelPerfect: true }); // blocks clicking on the moon that is behind the tree

    this.leaves = new Leaves(this);
    this.leaves.y = 640;
    this.add.existing(this.leaves);

    this.bats = new Bats(this);
    this.add.existing(this.bats);

    this.pumpkinDisplay = new Pumpkin(this);
    this.pumpkinDisplay.x = 125;
    this.pumpkinDisplay.y = -this.pumpkinDisplay.workingHeight;
    this.add.existing(this.pumpkinDisplay);

    this.happyHalloweenAudio = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);

    this.screenIntro();
  }

  screenIntro () {
    this.pumpkinDisplay.x = this.scale.width / 2 - this.pumpkinDisplay.workingWidth / 2;

    ABCyaPhaser.Tweens.addTween(this.pumpkinDisplay, 720, { delay: 500, y: this.scale.height - this.pumpkinDisplay.workingHeight }, Phaser.Math.Easing.Bounce.Out, () => this.startUI());
    this.time.delayedCall(500, this.playDelay, [SoundAssetKeys._whistle_down], this);
    this.time.delayedCall(750, this.leaves.bounceLeaves, null, this.leaves);
  }

  clickMoon () {
    this.bats.spawnBat();
  }

  playDelay (snd) {
    ABCyaPhaser.Audio.playOneShot(snd, this);
  }

  startUI () {
    if (!this.hud) {
      this.hud = new CarveHUD(this);
      this.add.existing(this.hud);
    }
    else {
      this.hud.activate(this.isNighttime);
      this.children.bringToTop(this.hud);
    }
    this.hud.on(CarveHUD.UNDO, this.handleUndo, this);
    this.hud.on(CarveHUD.RESET, this.handleReset, this);
    this.hud.on(CarveHUD.LIGHT, this.handleLightToggle, this);
    this.hud.on(CarveHUD.TOGGLE_TIME, this.handleTimeToggle, this);
    this.hud.x = 1;
    this.hud.y = this.scale.height - 120;
    this.pumpkinDisplay.enableDrawing();
  }

  handleUndo () {
    this.pumpkinDisplay.undoCut();
  }

  handleReset () {
    this.hud.off(CarveHUD.UNDO, this.handleUndo, this);
    this.hud.off(CarveHUD.RESET, this.handleReset, this);
    this.hud.off(CarveHUD.LIGHT, this.handleLightToggle, this);
    this.hud.off(CarveHUD.TOGGLE_TIME, this.handleTimeToggle, this);
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._click, this);
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._fling_pumpkin, this);
    this.tweens.killTweensOf(this.pumpkinDisplay);
    ABCyaPhaser.Tweens.addTween(this.pumpkinDisplay, 600, { y: this.scale.height + this.pumpkinDisplay.workingHeight }, Phaser.Math.Easing.Quadratic.In, () => this.switchPumpkins());
  }

  switchPumpkins () {
    const pid = this.whichPumpkin.getValue();
    this.pumpkinDisplay.newPumpkin(pid);
    this.pumpkinDisplay.y = -this.pumpkinDisplay.workingHeight;

    this.screenIntro();
  }

  handleLightToggle () {
    const lit = this.pumpkinDisplay.toggleCandle();
    this.hud.updateLightButton(lit);
    if (lit) {
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._match, this);
      this.happyHalloweenAudio.stop();

      ABCyaPhaser.Audio.playOneShot(LocalizationSoundAssetKeys._happy_halloween, this, 1, false, 0.3, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    }
    else {
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._blow, this);
    }
  }

  handleTimeToggle () {
    if (this.isNighttime) {
      this.dayTransition();
    }
    else {
      this.nightTransition();
    }
    this.hud.hideHud(this.isNighttime);
  }

  dayTransition () {
    this.nightSounds.stop();
    this.nightSounds.destroy();
    this.nightSounds = null;
    this.isNighttime = false;
    this.sun.angle = 90;
    this.moon.disableInteractive();

    ABCyaPhaser.Tweens.addTween(this.sun, 800, { delay: 100, angle: 0 }, Phaser.Math.Easing.Linear);

    ABCyaPhaser.Tweens.addTween(this.hillsNight, 800, { alpha: 0 }, Phaser.Math.Easing.Linear);

    ABCyaPhaser.Tweens.addTween(this.bgNight, 800, { alpha: 0 }, Phaser.Math.Easing.Linear);
    ABCyaPhaser.Tweens.addTween(this.moon, 560, { y: 500 }, Phaser.Math.Easing.Linear);
    ABCyaPhaser.Tweens.addTween(this.moonGlow, 800, { alpha: 0 }, Phaser.Math.Easing.Linear);

    this.pumpkinDisplay.turnDaytime();
    this.bats.hideIdleBats();

    ABCyaPhaser.Tweens.addColorTween(this.grass, 0xffffff, 600, Phaser.Math.Easing.Linear);
    ABCyaPhaser.Tweens.addTween(this.clouds, 600, { alpha: 0 }, Phaser.Math.Easing.Linear, () => this.finishDayTransition());
  }

  finishDayTransition () {
    this.grass.clearTint();

    this.clouds.changeToDay();
    ABCyaPhaser.Tweens.addTween(this.clouds, 440, { alpha: 1 }, Phaser.Math.Easing.Linear);

    this.moonGlow.setVisible(false);
    this.moon.setVisible(false);
    this.hillsNight.setVisible(false);
    this.bgNight.setVisible(false);

    this.house.switchTime(this.isNighttime);
    this.hud.activate(this.isNighttime);
  }

  nightTransition () {
    this.isNighttime = true;
    ABCyaPhaser.Tweens.addTween(this.sun, 800, { angle: -90 }, Phaser.Math.Easing.Linear);

    this.hillsNight.alpha = 0;
    this.hillsNight.setVisible(true);

    ABCyaPhaser.Tweens.addTween(this.hillsNight, 800, { alpha: 1 }, Phaser.Math.Easing.Linear);

    this.bgNight.alpha = 0;
    this.bgNight.setVisible(true);

    ABCyaPhaser.Tweens.addTween(this.bgNight, 800, { alpha: 1 }, Phaser.Math.Easing.Linear);

    this.moon.setVisible(true).disableInteractive();
    this.moon.x = 579;
    this.moon.y = 500;

    ABCyaPhaser.Tweens.addTween(this.moon, 800, { y: 44 }, Phaser.Math.Easing.Linear);

    this.moonGlow.setVisible(true).alpha = 0;
    ABCyaPhaser.Tweens.addTween(this.moonGlow, 800, { alpha: 1 }, Phaser.Math.Easing.Linear);

    this.pumpkinDisplay.turnDark();

    ABCyaPhaser.Tweens.addColorTween(this.grass, 0x000000, 600, Phaser.Math.Easing.Linear);
    ABCyaPhaser.Tweens.addTween(this.clouds, 600, { alpha: 0 }, Phaser.Math.Easing.Linear, () => this.finishNightTransition());

    this.nightSounds = ABCyaPhaser.Audio.createSound(this);
    ABCyaPhaser.Audio.playSound(SoundAssetKeys._night_scene, this.nightSounds, 1, true);
  }

  finishNightTransition () {
    this.clouds.changeToNight();
    this.clouds.alpha = 0;
    ABCyaPhaser.Tweens.addTween(this.clouds, 600, { alpha: 1 }, Phaser.Math.Easing.Linear);

    this.house.switchTime(this.isNighttime);
    this.handleLightToggle();

    this.hud.activate(this.isNighttime);
    this.bats.bringIdleBats();

    this.moon.setInteractive();
  }

  update(time, dt) {
    const dtMs = dt / 1000;
    this.clouds.tick(dtMs);
    this.pumpkinDisplay.tick(dtMs);
    this.house.tick(dtMs);
    if (this.isNighttime) {
      this.bats.tick(dtMs);
      if (this.scareWaiter <= 0) {
        const r = Phaser.Math.Between(1,100);
        if (r < 40) {
          ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._growl, this);
        }
        else {
          ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._scary_strings, this);
        }

        this.scareWaiter = Phaser.Math.Between(8000,18000) / 1000;
      }
      else {
        this.scareWaiter -= dtMs;
      }
    }
  }
}